import { createSlice } from '@reduxjs/toolkit'

import {
  activateRuleWithoutValidation,
  fetchTriggerRules,
  createTriggerRule,
  getTriggerRule,
  saveTriggerRule,
  setTriggerRuleTitle,
  setTriggerRuleStatusNotification,
  updateTriggerRuleNotification,
  deleteTriggerRule,
  createdTriggerRuleNotification,
  triggerRuleTitleSetNotification,
} from 'apps/triggerRules/actions/triggerRulesActions'
import triggerRuleAdapter from 'apps/triggerRules/models/TriggerRule/adapter'
import { CurrentTriggerRule, ITriggerRule } from 'apps/triggerRules/types'
import { TriggerRuleSocketSchema } from 'apps/triggerRules/types/schemas'
import { TriggerRule } from 'shared/api/common/schemas/flow'
import { TriggerRuleId } from 'shared/api/common/schemas/flow/triggerRule'
import { createAsyncAction } from 'shared/lib/redux'
import { mergeData } from 'utils/factory'
import { navigatePollyfill, isPathnameInclude } from 'utils/router/tools'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'
import { linkURL } from 'utils/url'

interface TriggerRulesListState {
  items: ITriggerRule[]
  creating: boolean
  fetching: boolean
  current: Record<number | string, CurrentTriggerRule>
}

const initialState: TriggerRulesListState = {
  items: [],
  creating: false,
  fetching: false,
  current: {},
}

const triggerRuleListSlice = createSlice({
  name: 'triggerRules',
  initialState,
  reducers: {
    updateTriggerRule: (state, { payload }) => {
      state.current[payload.rule_id].hasChanges = true
      state.current[payload.rule_id].item = mergeData(
        state.current[payload.rule_id].item,
        payload.trigger_rule,
      )
      state.items = state.items.map((item) =>
        item.rule_id === payload.rule_id ? payload.trigger_rule : item,
      )
      return state
    },

    copiedNotification: (state, { payload }: { payload: TriggerRule }) => {
      const rule = state.items.find((item) => item.rule_id === payload.rule_id)
      if (!rule) {
        const copiedTriggerRule = triggerRuleAdapter(payload)
        state.items.unshift(copiedTriggerRule)
      }
      return state
    },
    deletedNotification: (state, { payload }: { payload: { id: TriggerRuleId } }) => {
      const { id } = payload

      state.items = state.items.filter((item) => item.rule_id !== id)

      return state
    },
  },

  extraReducers(builder) {
    builder.addCase(fetchTriggerRules.pending, (state) => {
      state.fetching = true
      return state
    })

    builder.addCase(fetchTriggerRules.rejected, (state) => {
      state.fetching = false
      return state
    })

    builder.addCase(fetchTriggerRules.fulfilled, (state, { payload }) => {
      state.items = payload
      state.fetching = false
      return state
    })

    builder.addCase(createTriggerRule.pending, (state) => {
      state.creating = true
      return state
    })

    builder.addCase(createTriggerRule.fulfilled, (state) => {
      state.creating = false
      return state
    })

    builder.addCase(createTriggerRule.rejected, (state) => {
      state.creating = false
      return state
    })

    builder.addCase(getTriggerRule.pending, (state, { meta }) => {
      state.current[meta.arg.id] = {
        fetching: true,
        item: null,
        hasChanges: false,
        error: null,
        saving: false,
        isDeleted: false,
      }
      return state
    })

    builder.addCase(getTriggerRule.fulfilled, (state, { payload, meta }) => {
      state.current[meta.arg.id].fetching = false
      state.current[payload.trigger_rule.rule_id].item = triggerRuleAdapter(payload.trigger_rule)
      return state
    })

    builder.addCase(getTriggerRule.rejected, (state, { meta, payload }) => {
      state.current[meta.arg.id].fetching = false
      state.current[meta.arg.id].error = payload
      return state
    })

    builder.addCase(saveTriggerRule.fulfilled, (state, { meta, payload }) => {
      if (!meta || !payload) {
        return state
      }

      if (state.current[meta.arg.ruleId]) {
        state.current[meta.arg.ruleId].hasChanges = false
      }

      return state
    })

    builder.addCase(setTriggerRuleTitle.fulfilled, (state, { meta, payload }) => {
      const current = state.current[meta.arg.id]
      if (current && current.item) {
        current.item.title = payload.title
        state.current[meta.arg.id] = current
      }

      state.items = state.items.map((item) =>
        item.rule_id === payload.rule_id ? { ...item, title: payload.title } : item,
      )

      return state
    })

    builder.addCase(deleteTriggerRule.fulfilled, (state, { payload }) => {
      const { id } = payload

      state.items = state.items.filter((item) => item.rule_id !== id)

      if (state.current[id]) {
        state.current[id].isDeleted = true
      }
      return state
    })

    builder.addCase(activateRuleWithoutValidation.fulfilled, (state, { meta }) => {
      const { id } = meta.arg
      const current = state.current[id]

      if (current && current.item) {
        current.item.isPendingStatusChange = true
        state.current[id] = current
      }

      state.items = state.items.map((item) =>
        item.rule_id === id ? { ...item, isPendingStatusChange: true } : item,
      )

      return state
    })

    builder.addCase(activateRuleWithoutValidation.rejected, (state, { meta }) => {
      const { id } = meta.arg
      const current = state.current[id]

      if (current && current.item) {
        current.item.isPendingStatusChange = false
        state.current[id] = current
      }

      state.items = state.items.map((item) =>
        item.rule_id === id ? { ...item, isPendingStatusChange: false } : item,
      )

      return state
    })

    builder.addCase(setTriggerRuleStatusNotification, (state, { payload }) => {
      const { done, result } = payload
      const id = result.rule_id
      const current = state.current[id]

      if (current && current.item) {
        current.item.isPendingStatusChange = !done
        state.current[id] = current
      }

      state.items = state.items.map((item) =>
        item.rule_id === id ? { ...item, isPendingStatusChange: !done } : item,
      )

      return state
    })

    builder.addCase(updateTriggerRuleNotification, (state, { payload }) => {
      const updatedTriggerRule = triggerRuleAdapter(payload)
      const stateCurrent = state.current[payload.rule_id]?.item

      if (stateCurrent) {
        const merged = mergeData(stateCurrent, updatedTriggerRule)
        state.current[payload.rule_id].item = merged
      }

      state.items = state.items.map((item) =>
        item.rule_id === payload.rule_id ? mergeData(item, updatedTriggerRule) : item,
      )

      return state
    })
    builder.addCase(createdTriggerRuleNotification, (state, { payload }) => {
      const createdTriggerRule = triggerRuleAdapter(payload)

      state.current[createdTriggerRule.id] = {
        item: createdTriggerRule,
        fetching: false,
        saving: false,
        hasChanges: false,
        isDeleted: false,
        error: null,
      }

      state.items.unshift(createdTriggerRule)
      return state
    })
    builder.addCase(triggerRuleTitleSetNotification, (state, { payload }) => {
      const current = state.current[payload.rule_id]
      if (current && current.item) {
        current.item.title = payload.title
        current.item.caption = payload.caption
        state.current[payload.rule_id] = current
      }

      state.items = state.items.map((item) =>
        item.rule_id === payload.rule_id
          ? { ...item, title: payload.title, caption: payload.caption }
          : item,
      )
    })
  },
})

const { reducer: triggerRulesReducer, actions: triggerRulesActions } = triggerRuleListSlice
const { copiedNotification, deletedNotification, updateTriggerRule } = triggerRulesActions

const deletedNotificationAndClose = createAsyncAction(
  'triggerRules/deletedNotificationAndClose',
  async ({ id }: { id: TriggerRuleId }, { dispatch }) => {
    await dispatch(deletedNotification({ id }))
    if (isPathnameInclude(linkURL(`rules/${id}`))) {
      navigatePollyfill(linkURL('cms#rules'))
    }
  },
)

anotherTabNotificationsListener.on(
  TriggerRuleSocketSchema.setTriggerRuleStatus,
  ({ status }, dispatch) => {
    dispatch(updateTriggerRuleNotification(status.result))
    dispatch(setTriggerRuleStatusNotification(status))
  },
)

anotherTabNotificationsListener.on(TriggerRuleSocketSchema.update, ({ model }, dispatch) => {
  dispatch(updateTriggerRuleNotification(model.trigger_rule))
})

anotherTabNotificationsListener.on(TriggerRuleSocketSchema.created, ({ model }, dispatch) => {
  dispatch(createdTriggerRuleNotification(model))
})

anotherTabNotificationsListener.on(TriggerRuleSocketSchema.copied, ({ model }, dispatch) => {
  dispatch(copiedNotification(model))
})

anotherTabNotificationsListener.on(TriggerRuleSocketSchema.titleSet, ({ model }, dispatch) => {
  dispatch(triggerRuleTitleSetNotification(model))
})

anotherTabNotificationsListener.on(TriggerRuleSocketSchema.deleted, ({ model }, dispatch) => {
  dispatch(deletedNotificationAndClose({ id: model.rule_id }))
})

export type TriggerRulesState = TriggerRulesListState

export { triggerRulesReducer, deletedNotification, copiedNotification, updateTriggerRule }
